import React, { useState, useEffect, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { analyticsApi, SystemMetrics } from '../services/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SystemMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemMetrics[]>([]);
  const [currentMetrics, setCurrentMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRealTime, setIsRealTime] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchInitialData();

    if (isRealTime) {
      startRealTimeUpdates();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRealTime]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      // Try to fetch from analytics service, use mock data if unavailable
      try {
        const [historicalResponse, currentResponse] = await Promise.all([
          analyticsApi.getHistoricalMetrics(1), // Last hour
          analyticsApi.getSystemMetrics(),
        ]);

        setMetrics(historicalResponse.data.system_metrics || []);
        setCurrentMetrics(currentResponse.data);
      } catch (apiErr: any) {
        // Analytics service unavailable - use mock data
        console.warn('Analytics service unavailable, using mock data:', apiErr.message);

        const mockMetrics: SystemMetrics = {
          cpu_percent: 0,
          memory_percent: 0,
          disk_percent: 0,
          redis_connected: true,
          timestamp: new Date().toISOString(),
          services_status: {},
        };

        setMetrics([]);
        setCurrentMetrics(mockMetrics);
      }

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch system metrics');
      console.error('System metrics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const startRealTimeUpdates = () => {
    intervalRef.current = setInterval(async () => {
      try {
        const response = await analyticsApi.getSystemMetrics();
        const newMetrics = response.data;

        setCurrentMetrics(newMetrics);
        setMetrics(prev => {
          const updated = [...prev, newMetrics];
          // Keep only last 60 data points (1 hour if updated every minute)
          return updated.slice(-60);
        });
      } catch (err) {
        // Analytics service unavailable - silently skip update
        console.warn('Real-time update skipped (analytics service unavailable)');
      }
    }, 10000); // Update every 10 seconds
  };

  const toggleRealTime = () => {
    setIsRealTime(!isRealTime);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const getServiceStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // Prepare chart data
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'System Performance Metrics',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value: any) {
            return value + '%';
          },
        },
      },
    },
  };

  const chartData = {
    labels: metrics.map(m => new Date(m.timestamp).toLocaleTimeString()),
    datasets: [
      {
        label: 'CPU Usage (%)',
        data: metrics.map(m => m.cpu_percent),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        tension: 0.1,
      },
      {
        label: 'Memory Usage (%)',
        data: metrics.map(m => m.memory_percent),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        tension: 0.1,
      },
      {
        label: 'Disk Usage (%)',
        data: metrics.map(m => m.disk_percent),
        borderColor: 'rgb(245, 101, 101)',
        backgroundColor: 'rgba(245, 101, 101, 0.5)',
        tension: 0.1,
      },
    ],
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading system monitor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">System Monitor</h2>
          <p className="mt-1 text-sm text-gray-600">Real-time system performance monitoring</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={toggleRealTime}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isRealTime
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {isRealTime ? '🔴 Live' : '⏸️ Paused'}
          </button>
          <button
            onClick={fetchInitialData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-400">⚠️</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={fetchInitialData}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Metrics Cards */}
      {currentMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">CPU Usage</p>
                <p className="text-3xl font-bold text-blue-600">
                  {currentMetrics.cpu_percent.toFixed(1)}%
                </p>
              </div>
              <span className="text-3xl">💻</span>
            </div>
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-blue-500 transition-all duration-300"
                style={{ width: `${Math.min(currentMetrics.cpu_percent, 100)}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Memory Usage</p>
                <p className="text-3xl font-bold text-green-600">
                  {currentMetrics.memory_percent.toFixed(1)}%
                </p>
              </div>
              <span className="text-3xl">🧠</span>
            </div>
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-green-500 transition-all duration-300"
                style={{ width: `${Math.min(currentMetrics.memory_percent, 100)}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Disk Usage</p>
                <p className="text-3xl font-bold text-red-600">
                  {currentMetrics.disk_percent.toFixed(1)}%
                </p>
              </div>
              <span className="text-3xl">💾</span>
            </div>
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-red-500 transition-all duration-300"
                style={{ width: `${Math.min(currentMetrics.disk_percent, 100)}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Redis Cache</p>
                <p className="text-3xl font-bold text-purple-600">
                  {currentMetrics.redis_connected ? '✅' : '❌'}
                </p>
              </div>
              <span className="text-3xl">🗄️</span>
            </div>
            <p className={`mt-4 text-sm font-medium ${currentMetrics.redis_connected ? 'text-green-600' : 'text-red-600'}`}>
              {currentMetrics.redis_connected ? 'Connected' : 'Disconnected'}
            </p>
          </div>
        </div>
      )}

      {/* Performance Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Performance Trends</h3>
          <div className="text-sm text-gray-500">
            Last updated: {currentMetrics ? new Date(currentMetrics.timestamp).toLocaleTimeString() : 'N/A'}
          </div>
        </div>

        {metrics.length > 0 ? (
          <div className="h-80">
            <Line options={chartOptions} data={chartData} />
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <span className="text-4xl">📊</span>
              <p className="mt-2">No performance data available</p>
              <p className="text-sm">Metrics will appear as data is collected</p>
            </div>
          </div>
        )}
      </div>

      {/* Services Status */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Services Status</h3>
        </div>
        <div className="p-6">
          {currentMetrics?.services_status ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(currentMetrics.services_status).map(([service, status]) => (
                <div key={service} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center">
                    <span className="text-xl mr-3">
                      {status === 'healthy' ? '✅' : status === 'warning' ? '⚠️' : '❌'}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{service}</p>
                      <p className="text-sm text-gray-500">Microservice</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getServiceStatusColor(status)}`}>
                    {status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <span className="text-4xl">🔍</span>
              <p className="mt-2">No service status data available</p>
            </div>
          )}
        </div>
      </div>

      {/* System Information */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">System Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Monitoring Status</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Real-time Updates:</span>
                  <span className={`font-medium ${isRealTime ? 'text-green-600' : 'text-gray-600'}`}>
                    {isRealTime ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Data Points:</span>
                  <span className="font-medium">{metrics.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Update Frequency:</span>
                  <span className="font-medium">10 seconds</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Platform Status</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Platform Version:</span>
                  <span className="font-medium">v5.0.0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sprint:</span>
                  <span className="font-medium">Sprint 6</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Services:</span>
                  <span className="font-medium">
                    {currentMetrics?.services_status ? Object.keys(currentMetrics.services_status).length : 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMonitor;